import * as IoC from 'typescript-ioc';
import * as mobx from 'mobx';
import { IntlShape } from 'react-intl';
import { PagingViewModel } from '../../../global/_paging/paging-viewmodels/regular-paging.viewmodel';
import { alertServerError } from '../../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../../infrastructure/error-parsing';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../../../infrastructure/sub-windows';
import { SortOrder } from '../../../../api/shared.dto';
import { AttendeeForm } from '../_attendees-list/_attendee-form/attendee-form';
import { IAlertsService } from '../../../../infrastructure/alerts';
import { JobType } from '../../../../api/jobs.dao';

import { IGNORE_ATTENDEES, IOrganizationDetailsDTO, IOrganizationsDAO, IOrganizationsListDTO, TGetOrganizationsSortKeys, } from '../../../../api/organizations.dao';
import { MINIMAL_SEARCH_LENGTH } from '../../../../constants/common.constants';

import { isPathIncludesInCurrentRoute } from '../../../../infrastructure/utils/common.utils';
import { IRouter } from '../../../../infrastructure/router';
import { RouteParams, Routes } from '../../../../routes-table';
import { AttendeeListViewModel } from '../_attendees-list/attendees-list.viewmodel';
import { EventExecutionRuntime } from '../../../../domain/jobs/implementations/event-execution-runtime';
import { OrganizationForm } from './_organization-form/organization-form';
import { OpenFrom, TRACKING_EVENTS } from '../../../../tracking-service/tracking-values';
import TrackingServiceProvider from '../../../../tracking-service/tracking-service-provider';

class OrganizationsListViewModel {
    public setFetchMeetingsList(fetchMeetingList: () => Promise<void>): void {
        this.fetchMeetingsList = fetchMeetingList;
    }
    public setFetchRoomsWithConnections(fetchRoomsWithConnections: () => Promise<void>): void {
        this.fetchRoomsWithConnections = fetchRoomsWithConnections;
    }
    public setFetchRoomsList(fetchRoomsList: () => Promise<void>): void {
        this.fetchRoomsList = fetchRoomsList;
    }

    public setAssetsOrganizationsList = (fetchAssetsOrganizations: () => Promise<void>) => {
        this.fetchAssetsOrganizations = fetchAssetsOrganizations;
    };

    @mobx.observable
    private organizationIdToSubWindowMap: { [index: string]: ISubWindowFacade };

    @mobx.observable
    private organizations: IOrganizationDetailsDTO[];

    @mobx.observable
    private fetchingOrganizations: boolean;

    @mobx.observable
    private sortField: TGetOrganizationsSortKeys;

    @mobx.observable
    private sortOrder: SortOrder;

    @mobx.observable
    private organizationsCSVFile;

    @mobx.observable
    private organizationListItemWithAttendeesHovered: number;

    private paginationVM: PagingViewModel;

    @mobx.observable
    private attendeeIdToEditPopupMap: { [key: string]: ISubWindowFacade };

    @mobx.observable
    private isUploadCSVModalOpen: boolean;

    @mobx.observable
    public searchQuery: string;

    @mobx.observable
    private attendeeListVM: AttendeeListViewModel;

    public deleteOrganizationAlertKey: string;

    public fetchMeetingsList: () => Promise<void>;

    public fetchRoomsWithConnections: () => Promise<void>;

    public fetchRoomsList: () => Promise<void>;

    public fetchAssetsOrganizations: () => Promise<void>;

    @mobx.observable
    private readonly organizationSubWindows: Map<number, ISubWindowFacade> = new Map();
    @mobx.observable
    private readonly attendeeSubWindows: Map<number, ISubWindowFacade> = new Map();

    public constructor(
        public readonly accountId: number,
        public readonly eventId: number,
        private readonly intl: IntlShape,
        private fetchAttendees: () => void,
        attendeeIdToEditPopupMap: { [key: string]: ISubWindowFacade },
        @IoC.Inject private readonly organizationsDAO = IoC.Container.get(IOrganizationsDAO),
        @IoC.Inject private readonly errorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly subWindowsService = IoC.Container.get(ISubWindowsService),
        @IoC.Inject private readonly alertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly executionRuntime: EventExecutionRuntime = IoC.Container.get(EventExecutionRuntime),
        @IoC.Inject private readonly routingService: IRouter<RouteParams> = IoC.Container.get(IRouter),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),

    ) {
        this.organizations = [];
        this.paginationVM = new PagingViewModel();
        this.sortField = 'name';
        this.sortOrder = SortOrder.Asc;
        this.organizationIdToSubWindowMap = {};
        this.organizationListItemWithAttendeesHovered = null;
        this.attendeeIdToEditPopupMap = attendeeIdToEditPopupMap;
        mobx.reaction(this.paginationVM.getCurrentPage, this.fetchOrganizations);
        this.searchQuery = '';
        this.attendeeListVM = null;

        this.executionRuntime.onComplete(JobType.DeleteOrganization, async () => {
            await this.checkRouteAndFetch();
            this.closeAllOrganizationsModals();
        });
        this.executionRuntime.onComplete(JobType.OrganizationsUpload, this.handleImportOrganizationsJobCompletion);
        this.executionRuntime.onComplete(JobType.EventBlueprintTrigger, async () => await this.initialize());
    }

    @mobx.action
    public initialize(): void {
        this.organizations = [];
    }

    @mobx.action
    public setOrganizationListItemWithAttendeesHovered = (organizationId: number) => {
        this.organizationListItemWithAttendeesHovered = organizationId;
    };

    @mobx.action.bound
    private handleImportOrganizationsJobCompletion = async (): Promise<void> => {
        this.setUploadCSVModalOpen(false);
        this.closeAllOrganizationsModals();
        await this.fetchOrganizations();
    };

    @mobx.action.bound
    private onDeleteOrganization = async (organizationId: number): Promise<void> => {
        const job = this.executionRuntime.getJobByType(JobType.DeleteOrganization);
        job.onComplete(this.checkRouteAndFetch);
        await job.trigger({ organizationId: organizationId });
        this.closeOrganizationPopup(organizationId);
    };

    public composeUploadOrganizationsButtonProperties(): { text: string; disabled: boolean } {
        return {
            text: 'Upload Attendees CSV',
            disabled: !this.executionRuntime.isReady()
        };
    }

    @mobx.action.bound
    private closeAllOrganizationsModals() {
        for (const attendeeId of Object.keys(this.attendeeIdToEditPopupMap)) {
            this.attendeeIdToEditPopupMap[attendeeId].close();
        }

        for (const organizationId of Object.keys(this.organizationIdToSubWindowMap)) {
            this.organizationIdToSubWindowMap[organizationId].close();
        }
    }


    @mobx.action.bound
    public setUploadCSVModalOpen = (value: boolean) => {
        this.isUploadCSVModalOpen = value;
        this.organizationsCSVFile = null;
    };

    public isOrganizationListItemWithAttendeesHovered = (organizationId: number) => {
        return organizationId === this.organizationListItemWithAttendeesHovered;
    };

    @mobx.action
    public editAttendee = (attendeeId: number) => {
        const existingSubWindowForThisAttendee = this.attendeeIdToEditPopupMap[attendeeId];
        let shallOpenNewSubWindow = true;
        if (existingSubWindowForThisAttendee && existingSubWindowForThisAttendee.getStatus() !== SubWindowStatus.Closed) {
            // if we successfully maximized existing one
            existingSubWindowForThisAttendee.maximize();
            shallOpenNewSubWindow = false;
        }
        if (shallOpenNewSubWindow) {
            this.attendeeIdToEditPopupMap[attendeeId] = this.subWindowsService.showSubWindow({
                title: 'Edit Attendee',
                componentToHost: AttendeeForm,
                componentToHostProps: {
                    deleteAttendeeOnList: () => {
                        this.attendeeIdToEditPopupMap[attendeeId].close();
                        this.attendeeListVM.removeAttendeeFromList(attendeeId);
                    },
                    setDeleteAttendeeAlertKey: key => this.attendeeListVM.setDeleteAttendeeAlertKey(key),
                    saveCompletedCallback: () => this.checkRouteAndFetch(),
                    eventId: this.eventId,
                    accountId: this.accountId,
                    intl: this.intl,
                    attendeeId: attendeeId,
                    createOrganizationFromForm: this.showCreateOrganization,
                    openFrom: OpenFrom.Organizations,
                },
            });
        }
    };

    public checkRouteAndFetch = async () => {
        const route = this.routingService.router.getState();

        if (isPathIncludesInCurrentRoute(Routes.OrganizationsList, route)) {
            this.fetchOrganizations();
        }
        if (isPathIncludesInCurrentRoute(Routes.AttendeesList, route)) {
            this.fetchAttendees();
        }
        if (isPathIncludesInCurrentRoute(Routes.Meetings, route)) {
            await this.fetchMeetingsList();
        }
        if (isPathIncludesInCurrentRoute(Routes.ConnectionsList, route)) {
            await this.fetchRoomsWithConnections();
        }
        if (isPathIncludesInCurrentRoute(Routes.RoomsList, route)) {
           await this.fetchRoomsList();
        }
        if (isPathIncludesInCurrentRoute(Routes.Assets, route)) {
            await this.fetchAssetsOrganizations();
        }
    };

    public getUploadCSVModalOpen = () => {
        return this.isUploadCSVModalOpen;
    };

    public importOrganizationsCSV = async () => {
        const job = this.executionRuntime.getJobByType(JobType.OrganizationsUpload);
        job.onComplete(this.handleImportOrganizationsJobCompletion);

        try {
            await job.trigger({ file: this.organizationsCSVFile });
            this.setUploadCSVModalOpen(false);
        } finally {
            this.setOrganizationsCSVFile(null);
        }
    };

    public getCSVFileName = () => {
        return this.organizationsCSVFile ? this.organizationsCSVFile.name : '';
    };

    @mobx.action.bound
    public setOrganizationsCSVFile = (file: File) => {
        this.organizationsCSVFile = file;
    };

    public getPagingVM() {
        return this.paginationVM;
    }

    public isFetchingOrganizations = () => {
        return this.fetchingOrganizations;
    };

    @mobx.action
    public setSearchQuery = (query: string) => {
        this.searchQuery = query;

        if (query.trim().length >= MINIMAL_SEARCH_LENGTH) {
            this.paginationVM.selectPage(1);
            this.fetchOrganizations();
        }
    };

    @mobx.action
    public clearSearchQuery = () => {
        this.searchQuery = '';
        this.paginationVM.selectPage(1);
        this.fetchOrganizations();
    };

    public getOrganizations = () => {
        return this.organizations;
    };

    @mobx.action.bound
    private onFetchAttendeesSuccess(data: IOrganizationsListDTO) {
        this.organizations = data.content;
        this.fetchingOrganizations = false;
        this.paginationVM.setTotalRecords(data.totalElements);
    }

    @mobx.action
    private onFetchAttendeesFail(error) {
        this.fetchingOrganizations = false;
        const errorMessage = this.errorParsing.extractMessage(error);
        alertServerError('API Error fetching attendee records', errorMessage);
    }


    @mobx.action
    public fetchOrganizations = async () => {
        this.fetchingOrganizations = true;
        try {
            const result = await this.organizationsDAO.getOrganizations({
                accountId: this.accountId,
                eventId: this.eventId,
                sortOrder: this.sortOrder,
                sortField: this.sortField,
                query: this.searchQuery?.trim() || '',
                size: this.paginationVM.getPageSize(),
                page: this.paginationVM.getCurrentPageForBE(),
                ignoreAttendees: !IGNORE_ATTENDEES
            });
            this.onFetchAttendeesSuccess(result);
        } catch (error) {
            this.onFetchAttendeesFail(error);
        }
    };

    @mobx.action
    public setSortParams = (sortField: TGetOrganizationsSortKeys, sortOrder: SortOrder) => {
        this.sortField = sortField;
        this.sortOrder = sortOrder;
        this.fetchOrganizations();
    };

    public getSortField = () => {
        return this.sortField;
    };

    public getSortOrder = () => {
        return this.sortOrder;
    };

    public showCreateOrganization = (onOrganizationListUpdated?: (shouldUpdateOrganizatiosList: boolean) => Promise<IOrganizationsListDTO>) => {
        this.subWindowsService.showSubWindow({
            title: 'New Organization',
            componentToHost: OrganizationForm,
            componentToHostProps: {
                updateOrganizationOnList: () => {
                    onOrganizationListUpdated?.(true);
                    this.checkRouteAndFetch();
                },
                eventId: this.eventId,
                accountId: this.accountId,
                openFrom: OpenFrom.Organizations,
            },
        });
    };

    @mobx.action
    public showUpdateOrganization = (organizationId: number) => {
        const existingSubWindowForThisOrganization = this.organizationIdToSubWindowMap[organizationId];
        let shallOpenNewSubWindow = true;
        if (existingSubWindowForThisOrganization && existingSubWindowForThisOrganization.getStatus() !== SubWindowStatus.Closed) {
            existingSubWindowForThisOrganization.maximize();
            shallOpenNewSubWindow = false;
        }

        if (shallOpenNewSubWindow) {
            this.organizationIdToSubWindowMap[organizationId] = this.subWindowsService.showSubWindow({
                title: this.intl.formatMessage({
                    defaultMessage: 'Edit Organization',
                    description: 'Edit Organization sub-window header',
                    id: 'oe.organization.editSubWindowHeader'
                }),
                componentToHost: OrganizationForm,
                componentToHostProps: {
                    updateOrganizationOnList: () => this.checkRouteAndFetch(),
                    closeOrganizationPopup: () => this.closeOrganizationPopup(organizationId),
                    eventId: this.eventId,
                    accountId: this.accountId,
                    organizationId: organizationId,
                    onOrganizationDeletion: this.onDeleteOrganization.bind(this, organizationId),
                    openFrom: OpenFrom.Organizations,
                },
            });
        }
    };

    private closeOrganizationPopup(organizationId: number) {
        this.organizationIdToSubWindowMap[organizationId].close();
        this.closeOrganizationAttendeesPopups(organizationId);
    }

    private closeOrganizationAttendeesPopups(organizationId: number): void {
        if (!this.attendeeListVM) return;
        this.getAttendeesByOrganizationId(organizationId)
            .forEach(attendee => this.attendeeListVM.closeEditAttendeeForm(attendee.attendeeId));
    }

    private getOrganizationById = (id: number): IOrganizationDetailsDTO => this.organizations.find(() => id);
    private getAttendeesByOrganizationId = (id: number): IOrganizationDetailsDTO['attendees'] => this.getOrganizationById(id)?.attendees ?? [];


    public isOrganizationEditWindowOpen(organizationId: number) {
        return this.organizationIdToSubWindowMap[organizationId] &&
            this.organizationIdToSubWindowMap[organizationId].getStatus() !== SubWindowStatus.Closed;
    }

    @mobx.action
    public clearOrganizations = () => {
        this.organizations = [];
    };

    public isOrganizationsListEmpty() {
        return this.organizations.length === 0;
    }

    public downloadOrganizationsCSV = () => {
        this.organizationsDAO.getOrganizationsCSV({
            accountId: this.accountId,
            eventId: this.eventId
        });
        this.trackingService.get().track(TRACKING_EVENTS.DOWNLOAD_ATTENDEES_CSV, {
            'Open From': OpenFrom.Organizations,
        });
    };

    public downloadOrganizationsLinksCSV = async () => {
        try {
            await this.organizationsDAO.getOrganizationsLinksCSV({
                accountId: this.accountId,
                eventId: this.eventId
            });

            this.trackingService.get().track(TRACKING_EVENTS.DOWNLOAD_PRESENTER_ORG_LINKS, {
                'Open From': OpenFrom.Organizations,
            });
        } catch (error) {
            const errorMessage = this.errorParsing.extractMessage(error);
            alertServerError('Error', errorMessage);
        }
    };

    @mobx.action
    public setAttendeeListVM(attendeeListVM: AttendeeListViewModel) {
        this.attendeeListVM = attendeeListVM;
    }

    @mobx.action
    public handleAddAttendee(organizationId: number) {
        this.attendeeListVM.addAttendee(OpenFrom.Organizations, undefined, organizationId);
    }

    public setDeleteOrganizationAlertKey = (deleteOrganizationAlertKey: string): void => {
        this.deleteOrganizationAlertKey = deleteOrganizationAlertKey;
    };
}
export { OrganizationsListViewModel };
